const CSRF_STORAGE_KEY = 'csrf_token';
const CSRF_EXPIRY_KEY = 'csrf_token_expiry';
const TOKEN_LIFETIME = 60 * 60 * 1000; // 1 hour in milliseconds

let csrfToken = null;

/**
 * Initialize CSRF token from server
 * @param {string} hostname - The API hostname
 * @returns {Promise<string>} The CSRF token
 */
export const initializeCSRFToken = async (hostname) => {
  try {
    // Check if we have a valid cached token first
    const cachedToken = getCachedToken();
    if (cachedToken) {
      csrfToken = cachedToken;
      console.log('Using cached CSRF token');
      return csrfToken;
    }

    console.log('Fetching new CSRF token from:', `${hostname}/api/csrf/token`);

    // Fetch new token from server
    const response = await fetch(`${hostname}/api/csrf/token`, {
      method: 'GET',
      credentials: 'include', // Important: include cookies
      headers: {
        'Accept': 'application/json',
      }
    });

    if (!response.ok) {
      throw new Error(`Failed to fetch CSRF token: ${response.status} ${response.statusText}`);
    }

    const data = await response.json();
    csrfToken = data.token;
    
    // Cache the token with expiry
    sessionStorage.setItem(CSRF_STORAGE_KEY, csrfToken);
    sessionStorage.setItem(CSRF_EXPIRY_KEY, Date.now() + TOKEN_LIFETIME);
    
    console.log('CSRF token initialized successfully');
    return csrfToken;
  } catch (error) {
    console.error('Failed to initialize CSRF token:', error);
    throw error;
  }
};

/**
 * Get cached token if still valid
 * @returns {string|null} The cached token or null if expired/missing
 */
const getCachedToken = () => {
  const token = sessionStorage.getItem(CSRF_STORAGE_KEY);
  const expiry = sessionStorage.getItem(CSRF_EXPIRY_KEY);
  
  if (token && expiry && Date.now() < parseInt(expiry, 10)) {
    return token;
  }
  
  // Token expired or doesn't exist
  clearCSRFToken();
  return null;
};

/**
 * Get current CSRF token
 * @returns {string|null} The current CSRF token
 */
export const getCSRFToken = () => {
  // Try memory first
  if (csrfToken) return csrfToken;
  
  // Try cache
  const cachedToken = getCachedToken();
  if (cachedToken) {
    csrfToken = cachedToken;
    return csrfToken;
  }
  
  console.warn('CSRF token not available. Call initializeCSRFToken() first.');
  return null;
};

/**
 * Clear CSRF token (call on logout or errors)
 */
export const clearCSRFToken = () => {
  csrfToken = null;
  sessionStorage.removeItem(CSRF_STORAGE_KEY);
  sessionStorage.removeItem(CSRF_EXPIRY_KEY);
};

/**
 * Refresh token if needed
 * @param {string} hostname - The API hostname
 * @returns {Promise<string>} The CSRF token
 */
export const refreshCSRFTokenIfNeeded = async (hostname) => {
  const cachedToken = getCachedToken();
  if (!cachedToken) {
    console.log('Token expired or missing, refreshing...');
    return await initializeCSRFToken(hostname);
  }
  return cachedToken;
};